export default {
  title: 'Components/Link Button',
  tags: ['autodocs'],
  parameters: {
    docs: {
      description: {
        component: `
To use the \`dda-link-button\` component, pass the following props:

\`\`\`html
<dda-link-button
    button_color="default-primary"
    start_icon="sentiment_satisfied"
    end_icon="arrow_forward"
    custom_class=""
    href=""
    target=""
    rel=""
    component_mode=""
    button_id="link-button"
    aria_label="link-Button"
    disabled="false"
    onclick="console.log('clicked')"
    >Button
</dda-link-button>
  \`\`\`

The component now properly handles disabled state, preventing navigation and click events when disabled is true.
`,
      },
    },
  },
  argTypes: {
    button_color: {
      control: { type: 'select' },
      options: [
        'default-primary','default-secondary','default-tertiary','default-link',
        'error-primary','error-secondary','error-tertiary','error-link',
        'onsurface-primary','onsurface-secondary','onsurface-tertiary','onsurface-link',
        'secondary','success','warning','surface','surface-variant','disabled',
      ],
      description: 'Selection of the color of the button',
    },
    size: {
      control: { type: 'select' },
      options: ['sm', 'md', 'lg', 'xl'],
      description: 'Size of the link button',
    },
    button_shape: {
      control: { type: 'select' },
      options: ['default', 'circle',],
      description: 'Options to display the button as square or circle',
    },
    icon_button_shape: {
      control: { type: 'select' },
      options: ['default', 'circle',],
      description: 'Options to display the icon as square or circle',
    },
    gap: {
      control: { type: 'select' },
      options: ['1', '2'],
    },
    link_button_text: {
      control: { type: 'text' },
      description: 'Text content for the link button',
    },
    link_button_title: {
      control: { type: 'text' },
      description: 'Title for the link button',
    },
    custom_class: {
      control: { type: 'text' },
      description: 'Custom class for the link button component',
    },    
    href: {
      control: { type: 'text' },
      description: 'Href to point to when the button is clicked',
    },
    target: {
      control: { type: 'text' },
      description: 'Target attribute for the link (e.g., _blank)',
    },
    rel: {
      control: { type: 'text' },
      description: 'Relationship attribute for the link (e.g., noopener noreferrer)',
    },
    component_mode: {
      control: { type: 'check' },
      options: ['light-mode'],
      description: 'Mode of the link button component',
    },
    button_id: {
      control: {type: 'text'},
      description: 'Button id for the link button component',
    },
    aria_label: {
      control: { type: 'text' },
      description: 'Aria label for accessibility',
    },
    disabled: {
      control: { type: 'boolean' },
      description: 'Whether the link button is disabled',
    },
    onclick:{
      control: { type: 'text' },
      description: 'event handler for button',
    },
  },
};

const Template = (args) => {
  const attributes = Object.entries(args)
    .filter(([key, value]) => value !== undefined && key !== 'text')
    .map(([key, value]) => {
      if (typeof value === 'boolean') {
        return value ? key : '';
      }
      return `${key}="${value}"`;
    })
    .filter(Boolean)
    .join(' ');

  return `<dda-link-button ${attributes}></dda-link-button>`;
};

export const DefaultPrimary = Template.bind({});
DefaultPrimary.args = {
  button_color: 'default-primary',
  start_icon: 'sentiment_satisfied',
  link_button_text: 'Button',
  end_icon: 'arrow_forward',
  link_button_title: '',
  button_id: 'link-button',
  href: '#',
  aria_label: 'link-Button',
  button_shape: 'default',
  size: 'md',
  gap: '1',
  target: '',
  rel: 'noopener noreferrer',
  custom_class: '',
  component_mode: '',
  disabled: false,
  onclick: "console.log('clicked')",
};

export const ErrorPrimary = Template.bind({});
ErrorPrimary.args = {
  button_color: 'error-primary',
  start_icon: 'sentiment_satisfied',
  link_button_text: 'Button',
  end_icon: 'arrow_forward',
  link_button_title: '',
  button_id: 'link-button',
  href: '#',
  aria_label: 'link-Button',
  button_shape: 'default',
  size: 'md',
  gap: '1',
  target: '',
  rel: 'noopener noreferrer',
  custom_class: '',
  component_mode: '',
  disabled: false,
  onclick: "console.log('clicked')",
};

export const SurfacePrimary = Template.bind({});
SurfacePrimary.args = {
  button_color: 'onsurface-primary',
  start_icon: 'sentiment_satisfied',
  link_button_text: 'Button',
  end_icon: 'arrow_forward',
  link_button_title: '',
  button_id: 'link-button',
  href: '#',
  aria_label: 'link-Button',
  button_shape: 'default',
  size: 'md',
  gap: '1',
  target: '',
  rel: 'noopener noreferrer',
  custom_class: '',
  component_mode: '',
  disabled: false,
  onclick: "console.log('clicked')",
};

export const Disabled = Template.bind({});
Disabled.args = {
  button_color: 'disabled',
  start_icon: 'sentiment_satisfied',
  link_button_text: 'Button',
  end_icon: 'arrow_forward',
  link_button_title: '',
  button_id: 'link-button',
  href: '#',
  aria_label: 'link-Button',
  button_shape: 'default',
  size: 'md',
  gap: '1',
  target: '',
  rel: 'noopener noreferrer',
  custom_class: '',
  component_mode: '',
  disabled: true,
  onclick: "console.log('clicked')",
};

export const IconButton = Template.bind({});
IconButton.args = {
  button_color: 'default-primary',
  start_icon: 'sentiment_satisfied',
  button_id: 'link-button',
  link_button_title: '',
  href: '#',
  aria_label: 'link-Button',
  icon_button_shape: "default",
  size: 'md',
  target: '',
  rel: 'noopener noreferrer',
  custom_class: '',
  component_mode: '',
  disabled: false,
  onclick: "console.log('clicked')",
};

export const IconButtonDisabled = Template.bind({});
IconButtonDisabled.args = {
  button_color: 'disabled',
  start_icon: 'sentiment_satisfied',
  button_id: 'link-button',
  link_button_title: '',
  href: '#',
  aria_label: 'link-Button',
  icon_button_shape: "default",
  size: 'md',
  target: '',
  rel: 'noopener noreferrer',
  custom_class: '',
  component_mode: '',
  disabled: true,
  onclick: "console.log('clicked')",
};

